import { APIBinderData, APIInsurancePolicy } from 'types/api.types';
import { BinderSectionData } from '../hooks/useBinderSectionData';
import { getInsuranceSourceLabel, processValidation } from '../utils/helpers';

export const processLapsesData = (
  binderReportData: APIBinderData,
  validation?: Validation,
): BinderSectionData => {
  const applicationData = binderReportData?.application_data;
  const quoteData = binderReportData?.quote_data;
  const insuranceData = binderReportData?.insurance_data?.data;
  const insuranceSource = binderReportData?.insurance_data?.source;
  const drivers = applicationData?.drivers || [];

  const driverLapsesData = drivers.map((driver, index: number) => {
    // Format duration
    const formatDuration = (months: number | string): string => {
      if (!months && months !== 0) return 'N/A';
      const monthsNum = Number(months);

      if (monthsNum === 0) return 'Less than 1 month';
      if (monthsNum === 1) return '1 month';
      if (monthsNum < 12) return `${monthsNum} months`;

      const years = Math.floor(monthsNum / 12);
      const remainingMonths = monthsNum % 12;

      if (remainingMonths === 0) {
        return years === 1 ? '1 year' : `${years} years`;
      }
      return `${years} year${years > 1 ? 's' : ''} ${remainingMonths} month${
        remainingMonths > 1 ? 's' : ''
      }`;
    };
    const policies = insuranceData?.[index]?.policies || [];

    // Get Quote lapses for this driver
    const quoteLapses = quoteData?.drivers?.[index]?.lapses || [];

    // Process AutoPlus lapses
    const processedAutoPlusPolicies = policies.map(
      (policy: APIInsurancePolicy) => ({
        driver: driver?.name || `Driver ${index + 1}`,
        startDate: policy?.recorded_start_date,
        endDate: policy?.cancellation_date || policy?.recorded_end_date,
        duration: formatDuration(
          policy?.duration_months + policy?.duration_years * 12,
        ),
        status: policy?.status || 'Not specified',
      }),
    );

    // Process Quote lapses
    const processedQuoteLapses = quoteLapses.map((lapse) => ({
      driver: driver?.name || `Driver ${index + 1}`,
      date: lapse?.date || '',
      reinstateDate: lapse?.re_instate_date || '',
      duration: formatDuration(lapse?.duration_months),
      description: lapse?.description || 'Not specified',
    }));

    return {
      driverIndex: index + 1,
      driverName: driver?.name || `Driver ${index + 1}`,
      autoPlusPolicies: processedAutoPlusPolicies,
      quoteLapses: processedQuoteLapses,
      totalLapses:
        processedAutoPlusPolicies.length + processedQuoteLapses.length,
      hasLapseHistory:
        processedAutoPlusPolicies.length > 0 || processedQuoteLapses.length > 0,
    };
  });

  // Check for recent lapses or concerning patterns
  const validationData = processValidation(validation);

  // Flag if there are any cancellations due to payment issues
  const hasPaymentCancellations = driverLapsesData.some((driver) =>
    driver.quoteLapses.some(
      (lapse) =>
        lapse.description?.toLowerCase().includes('defaulting') ||
        lapse.description?.toLowerCase().includes('cancelled'),
    ),
  );

  return {
    title: 'Lapses & Cancellations',
    description:
      'Review insurance coverage lapses and cancellations from multiple data sources for each driver.',
    isValid: validationData.isValid,
    message: validationData.message,
    data: {
      insuranceSource: getInsuranceSourceLabel(insuranceSource),
      drivers: driverLapsesData,
      hasPaymentCancellations,
    },
  };
};
